import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * The single error body returned by every CoreAPI endpoint.
 *
 * Mirrors `ErrorDetail` in
 * `code/golden_brick/src/apps/api/errors/http_errors.py` and the `ApiError`
 * schema in `openapi.json`. Branch on `code`; never on `message`, whose wording
 * the backend may change at any time.
 */
export interface ApiError {
  code: string;
  domain: string;
  message: string;
  details: ApiErrorDetail[];
}

export interface ApiErrorDetail {
  path: string[];
  message: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError => {
  return isObject(error) && 'status' in error;
};

const toDetails = (value: unknown): ApiErrorDetail[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    if (!isObject(entry) || !isString(entry.message)) {
      return [];
    }
    return [
      {
        path: Array.isArray(entry.path) ? entry.path.filter(isString) : [],
        message: entry.message,
      },
    ];
  });
};

/**
 * Reads the error envelope off an RTK Query error.
 *
 * Returns `undefined` for transport-level failures (network down, CORS) which
 * carry no envelope, letting callers tell "the API rejected this" apart from
 * "the API was unreachable".
 */
export const getApiError = (error: unknown): ApiError | undefined => {
  if (!isFetchBaseQueryError(error)) {
    return undefined;
  }

  const { data } = error;
  if (!isObject(data) || !isString(data.error) || !isString(data.message)) {
    return undefined;
  }

  return {
    code: data.error,
    domain: isString(data.domain) ? data.domain : '',
    message: data.message,
    details: toDetails(data.details),
  };
};

export const getApiErrorCode = (error: unknown): string | undefined => getApiError(error)?.code;
